/* eslint-disable camelcase */
import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { Button, tokens, Pagination, PageHeader } from '@hotjar/hotsauce';
import { Page } from '@hotjar/webapp/components/Page';
import { useSites } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useOrganizations } from '@hotjar/organization';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { SITE } from '@hotjar/webapp/constants/routes';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import NoSearchResultsPNG from '@hotjar/shared-assets/images/no-search-results.png';
import { useCurrentSiteAccount } from '@hotjar/account';
import { show as showTrackingModal } from '@hotjar/webapp/domains/activation/tracking-code/show-tracking-code';
import { useWindowTitle } from '@hotjar/webapp/hooks/useWindowTitle';
import { Organization } from './Organization/Organization';
import type { OrganizationsListItem } from './Organization/types';
import { addSitesToOrganizations } from './utils';
import { SearchProvider } from '../../../components/SearchProvider/SearchProvider';
import { SitesAndOrganizationPageLoader } from './SitesAndOrganizationsLoader';
import { Event, UiPosition } from './events';

const TRANSLATION_DOMAIN = 'account:sites-and-organizations';

const S = {
  SearchProvider: styled(SearchProvider)`
    margin-bottom: ${tokens.spacer.x3};
  `,
};

const MIN_PAGE_SIZE = 30;

export function SitesAndOrganizationPage(): React.ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const [listData, setListData] = useState<OrganizationsListItem[]>();
  const { query: sitesQuery } = useSites();
  const { data: user } = useCurrentUser();
  const { data: account } = useCurrentSiteAccount();
  const { data: organizations = [], error } = useOrganizations(user?.id);
  const { click, formSubmit } = useTrack();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);
  useWindowTitle(t('title'));

  const { data: sites } = sitesQuery;
  const account_id = account?.id;

  const createNewSite = (organizationId?: number) => {
    const from = organizationId ? UiPosition.ORGANIZATION : UiPosition.HEADER;
    click(Event.CLICK_NEW_SITE, from, { account_id, organization_id: organizationId });
    navigateTo(SITE.CREATE.path);
  };

  const updateListData = useCallback(
    (searchValue = '') => {
      if (organizations.length && sites) {
        const data = addSitesToOrganizations(organizations, sites, searchValue);
        setListData(data);
      }
    },
    [organizations, sites],
  );

  const handleSearchChange = (searchValue = '') => {
    if (searchValue) {
      formSubmit(Event.FILTERED_LIST, true, { account_id }, {});
    }
    updateListData(searchValue);
    // we need to reset to the first page after filtering results
    setCurrentPage(1);
  };

  useEffect(() => {
    updateListData();
  }, [sites, organizations, updateListData]);

  const paginatedData = listData?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <Page>
      <PageHeader
        title={t('title')}
        actions={
          <>
            <Button icon="plus" kind="primary" onClick={() => createNewSite()}>
              {t('new-site')}
            </Button>
            <Button
              icon="plus"
              kind="secondary"
              onClick={() => {
                click(Event.CLICK_NEW_ORGANIZATION, UiPosition.HEADER, { account_id });
                navigateTo(SITE.CREATE_ORGANIZATION.path);
              }}
            >
              {t('new-organization')}
            </Button>
          </>
        }
      />
      {error && (
        <EmptyState
          title={t('error.organization-failed')}
          subtitle={error.message}
          img={NoSearchResultsPNG}
        />
      )}
      {!error && (
        <S.SearchProvider
          placeholder={t('search.placeholder')}
          onSearchChanged={handleSearchChange}
        >
          {paginatedData === undefined ? (
            <SitesAndOrganizationPageLoader />
          ) : (
            <>
              {paginatedData.map((organization) => (
                <Organization
                  organization={organization}
                  sitesTotal={sites?.length ?? 0}
                  organizationsTotal={organizations?.length ?? 0}
                  key={organization.id}
                  onTrackingCodeClick={(site) => showTrackingModal(site, 2, 'Site List')}
                  onCreateSite={(organizationId) => createNewSite(organizationId)}
                />
              ))}
              {(organizations?.length ?? 0) >= MIN_PAGE_SIZE && (
                <Pagination
                  totalCount={listData?.length ?? 0}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              )}
            </>
          )}
        </S.SearchProvider>
      )}
    </Page>
  );
}
